import { Skeleton } from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { BeatLoader } from "react-spinners";
import { apiWrapWithErrorWithData } from "../../utilities/apiHelpers";
import {
  templatesDetailsApi,
  templatesUpdateEditorContent,
} from "../../utilities/apis/templates";
import { loadingStates } from "../../utilities/utilities";
import Editor from "../Editor";
import { getDocsToken } from "../../utilities/apis/agreements";
import constants from "../../utilities/constants";

function TemplateEditor() {
  const [placeholders, setPlaceholders] = useState();
  const editorRef = useRef();
  const [sfdtContent, setSfdtContent] = useState(null);
  const { templateId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isNewTemplate = Boolean(location.state?.isNew);
  const [templatesConfig, setTemplatesConfig] = useState({
    loading: loadingStates.LOADING,
    editorContent: null,
    templateTitle: "",
    id: templateId,
  });

  const [uiConfigs, setUiConfigs] = useState({
    loading: loadingStates.NO_ACTIVE_REQUEST,
  });
  const [titleEditable, setTitleEditable] = useState(false);
  const [confirmTitleEdit, setConfirmTitleEdit] = useState(false);
  const titleInputRef = useRef(null);

  const listPath = "/app/agreements_manager/templates/agreement";

  const getTemplate = async (tempId) => {
    const response = await apiWrapWithErrorWithData(
      templatesDetailsApi({ templateId: tempId }),
    );
    if (response?.success && response?.template) {
      const accessToken = await apiWrapWithErrorWithData(getDocsToken());
      const response2 = await fetch(
        `https://graph.microsoft.com/v1.0/users/${constants.userId}/drive/items/${response.template.quillDelta}/content`,
        {
          headers: {
            Authorization: `Bearer ${accessToken.data}`,
          },
        },
      );

      if (response2.headers.get("Content-Length") !== 0) {
        const blob = await response2.blob();
        const file = new File([blob], "doc.docx", {
          type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        });

        const formData = new FormData();
        formData.append("files", file);
        try {
          const sfdtResponse = await fetch(
            `${constants.wordeditorApi}/api/documenteditor/Import`,
            { method: "POST", body: formData },
          );

          const sfdt = await sfdtResponse.json();
          setSfdtContent(sfdt);
        } catch (error) {
          console.error("Error importing document:", error);
          setTemplatesConfig({
            ...templatesConfig,
            loading: loadingStates.NO_ACTIVE_REQUEST,
            editorContent: response.template.quillDelta,
            templateTitle: response.template.name,
          });
        }
      }
      setTemplatesConfig({
        ...templatesConfig,
        loading: loadingStates.NO_ACTIVE_REQUEST,
        editorContent: response.template.quillDelta,
        templateTitle: response.template.name,
      });
      setPlaceholders(response.template.fieldName || []);
    } else {
      setTemplatesConfig({
        ...templatesConfig,
        loading: loadingStates.NO_ACTIVE_REQUEST,
      });
    }
  };

  useEffect(() => {
    getTemplate(templateId);
  }, []);

  const saveTemplate = async (tempId) => {
    setUiConfigs({
      ...uiConfigs,
      loading: loadingStates.LOADING,
    });
    const response = await apiWrapWithErrorWithData(
      templatesUpdateEditorContent({
        templateId: tempId,
        quillDelta: templatesConfig.editorContent,
        templateTitle: templatesConfig.templateTitle,
        fieldName: placeholders,
      }),
    );
    if (response?.success) {
      setTemplatesConfig({
        ...templatesConfig,
        loading: loadingStates.NO_ACTIVE_REQUEST,
        editorContent: response.quillContent.quillDelta,
      });
      setUiConfigs({
        ...uiConfigs,
        loading: loadingStates.NO_ACTIVE_REQUEST,
      });
    } else {
      showNotification({
        color: "red",
        title: "Templates Details",
        message: "Failed to load template details.",
      });
      setUiConfigs({
        ...uiConfigs,
        loading: loadingStates.NO_ACTIVE_REQUEST,
      });
    }
  };

  const saveContent = async () => {
    if (!editorRef.current) return;

    const editor = editorRef.current.getDocumentEditor();
    const blob = await editor.saveAsBlob("Docx");

    if (!blob || blob.size === 0) {
      throw new Error("Generated document is empty");
    }
    try {
      setUiConfigs({
        ...uiConfigs,
        loading: loadingStates.LOADING,
      });
      const accessToken = await apiWrapWithErrorWithData(getDocsToken());
      const uploadResponse = await fetch(
        `https://graph.microsoft.com/v1.0/users/${constants.userId}/drive/items/${templatesConfig.editorContent}/content`,
        {
          method: "PUT",
          headers: { Authorization: `Bearer ${accessToken.data}` },
          body: blob,
        },
      );
      await saveTemplate(templateId);
      if (!uploadResponse.ok) {
        throw new Error("Failed to upload updated DOCX to OneDrive");
      }

      showNotification({
        color: "green",
        title: "Template Saved",
        message: "Template has been saved successfully.",
      });

      navigate(listPath);
    } catch (error) {
      console.error("Error saving document:", error);
      showNotification({
        color: "red",
        title: "Save Failed",
        message: "Failed to save template. Please try again.",
      });
      setUiConfigs({
        ...uiConfigs,
        loading: loadingStates.NO_ACTIVE_REQUEST,
      });
    }
  };

  const isLoading = templatesConfig.loading === loadingStates.LOADING;
  const isSaving = uiConfigs.loading === loadingStates.LOADING;

  const allowTitleEdit = () => {
    setTitleEditable(true);
    setConfirmTitleEdit(false);
    setTimeout(() => titleInputRef.current?.focus(), 0);
  };

  return (
    <div className="flex w-full flex-col gap-5 px-1 pb-8">
      <div className="flex flex-col gap-4 border-b border-slate-200 pb-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <button
            type="button"
            onClick={() => navigate(listPath)}
            className="mb-2 inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-800"
          >
            <svg viewBox="0 0 20 20" fill="none" className="h-4 w-4">
              <path
                d="M12.5 4.5L7 10l5.5 5.5"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Back to templates
          </button>
          <h1 className="text-xl font-semibold text-slate-900">
            {isNewTemplate ? "Create template" : "Edit template"}
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            {isNewTemplate
              ? "Add the document content and placeholders, then save."
              : "Update the title, document content, and placeholders, then save."}
          </p>
        </div>
        <button
          type="button"
          onClick={() => saveContent()}
          disabled={isLoading || isSaving}
          className="inline-flex h-10 min-w-[120px] items-center justify-center rounded-lg bg-[#46BDE1] px-5 text-sm font-medium text-white hover:bg-[#3aa8ca] disabled:opacity-60"
        >
          {isSaving ? <BeatLoader color="#fff" size={8} /> : "Save template"}
        </button>
      </div>

      {isLoading ? (
        <div className="flex flex-col gap-4">
          <Skeleton height={72} radius="md" />
          <Skeleton height={420} radius="md" />
        </div>
      ) : (
        <>
          <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5">
            <label
              htmlFor="template-title"
              className="mb-1.5 block text-sm font-medium text-slate-700"
            >
              Template title
            </label>
            <div className="flex items-center gap-2">
              <input
                id="template-title"
                ref={titleInputRef}
                type="text"
                value={templatesConfig.templateTitle}
                readOnly={!titleEditable}
                onChange={(e) =>
                  setTemplatesConfig({
                    ...templatesConfig,
                    templateTitle: e.target.value,
                  })
                }
                placeholder="Enter template title"
                className={`h-11 w-full rounded-lg border px-3 text-sm outline-none ${
                  titleEditable
                    ? "border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 focus:border-[#46BDE1] focus:ring-2 focus:ring-[#46BDE1]/20"
                    : "cursor-not-allowed border-slate-200 bg-slate-50 text-slate-600"
                }`}
              />
              {!titleEditable && (
                <button
                  type="button"
                  onClick={() => setConfirmTitleEdit(true)}
                  className="inline-flex h-11 shrink-0 items-center rounded-lg border border-slate-200 bg-white px-4 text-sm font-medium text-slate-700 hover:border-[#46BDE1] hover:text-[#1a8fb3]"
                >
                  Edit
                </button>
              )}
            </div>
          </div>
          {/* <div>
            <DocxToHtmlUploader
              htmlContent={templatesConfig.editorContent.content}
              setHtmlContent={setContent}
            />
          </div> */}
          <div className="overflow-hidden">
            <Editor
              placeholders={placeholders}
              setPlaceholders={setPlaceholders}
              ref={editorRef}
              initialContent={sfdtContent}
            />
          </div>
        </>
      )}
    </div>
  );
}

export default TemplateEditor;
